import { useState } from 'react';
import { AxiosResponse, AxiosError } from 'axios';
import { saveAs } from 'file-saver';
import { useGetApi } from '../Redux/Hooks/useGetApi';
import { IRequestParams } from '../Interfaces/IRequestParams';
import { errorToast, successToast } from '../Service-Utils/toastMessage';
import { ApiCallStatus, ApiCallStatusStates } from '../Types/ApiStatus';
import { FileDownloadParameters } from '../Types/FileDownload';
import { extractErrorMessage } from './messageHelper';

export const useFileDownload = ({ endpoint, fileDetails, isEnabled, showDefaultErrorToast = true, showDefaultSuccessToast = true, onSuccess, onError }: FileDownloadParameters): {
    fileDownloadStatus: ApiCallStatus
} => {
    const [fileDownloadStatus, setFileDownloadStatus] = useState<ApiCallStatus>(ApiCallStatusStates.LOADING);

    const handleError = (error: AxiosError) => {
        showDefaultErrorToast && errorToast(extractErrorMessage(error));
        setFileDownloadStatus(ApiCallStatusStates.ERROR);

        if (onError) {
            onError(error);
        }
    }

    const handleSuccess = (response: AxiosResponse, filename: string) => {
        showDefaultSuccessToast && successToast(`File ${filename} successfully downloaded.`);
        setFileDownloadStatus(ApiCallStatusStates.SUCCESS);

        if (onSuccess) {
            onSuccess(response);
        }
    }

    const saveFile = (response: AxiosResponse): string => {
        const contentDisposition: string | null | undefined = response.headers?.["Content-Disposition"] || response.headers?.["content-disposition"];
        let filename: string = `${fileDetails.fileNamePrefix}_${new Date().toISOString()}.${fileDetails.FileType}`;
        if (contentDisposition) {
            const match: RegExpExecArray | null = /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i.exec(contentDisposition);
            filename = match ? (match[1] || match[2]) : filename;
        }
        const rawContentType = response.headers?.["content-type"];
        const contentType: string | undefined =
            typeof rawContentType === "string" ? rawContentType : undefined;
        const blob = new Blob([response.data], { type: contentType });

        saveAs(blob, filename);

        return filename;
    }

    function onSuccessHandler(res: any) {
        if (res.isAxiosError) {
            handleError(res);

            return;
        }

        handleSuccess(res, saveFile(res));
    }

    function onErrorHandler(err: AxiosError) {
        handleError(err);
    }

    let requestParam: IRequestParams = {
        isEnabled: isEnabled,
        method: "get",
        onError: onErrorHandler,
        onSuccess: onSuccessHandler,
        responseType: "blob",
        service: endpoint,
        take: 1000000
    };

    useGetApi(requestParam);

    return {
        fileDownloadStatus
    };
}